import React, { useState, useEffect, useRef } from 'react';
import { Terminal as TerminalIcon, ExternalLink, CheckCircle } from 'lucide-react';
import { Terminal as XTerm } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import { WebglAddon } from 'xterm-addon-webgl';
import { challengeAPI } from '../../services/api';
import 'xterm/css/xterm.css';

interface TerminalProps {
  containerId: string | null;
  challengeId?: number;
  onValidationResult?: (result: { success: boolean; message: string; points_earned?: number }) => void;
  onSessionExpired?: () => void;
}

export const Terminal: React.FC<TerminalProps> = ({ containerId, challengeId, onValidationResult, onSessionExpired }) => {
  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<XTerm | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [validating, setValidating] = useState(false);
  const isMountedRef = useRef(true);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const MAX_RECONNECT_ATTEMPTS = 3;
  const INITIAL_RECONNECT_DELAY = 5000;
  const [reconnectFailed, setReconnectFailed] = useState(false);

  const validateSolution = async () => {
    if (!containerId || !challengeId) return;
    
    setValidating(true);
    try {
      const result = await challengeAPI.validateSolution(challengeId, containerId);
      if (onValidationResult) {
        onValidationResult(result);
      }
    } catch (error: any) {
      console.error('Validation failed:', error);
      if (onValidationResult) {
        onValidationResult({
          success: false,
          message: error.response?.data?.detail || 'Validation failed. Please try again.'
        });
    }
    } finally {
      setValidating(false);
    }
  };

  // Handle connection state updates
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Handle WebSocket connection
  useEffect(() => {
    if (!containerId || !terminalRef.current) return;

    let ws: WebSocket | null = null;
    let term: XTerm | null = null;

    const cleanup = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (ws?.readyState === WebSocket.OPEN) {
        ws.close(1000, 'Component unmounting');
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (term) {
        try {
          // Remove all addons first
          term.loadAddon(new FitAddon());
          term.loadAddon(new WebLinksAddon());
          // Don't load WebGL addon during cleanup
          
          // Clear the terminal
          term.clear();
          
          // Dispose the terminal
          term.dispose();
        } catch (error) {
          console.error('Error during terminal cleanup:', error);
        }
      }
    };

    try {
      // Initialize xterm.js
      term = new XTerm({
        cursorBlink: true,
        fontSize: 14,
        fontFamily: 'Menlo, Monaco, "Courier New", monospace',
        theme: {
          background: '#1a1a1a',
          foreground: '#ffffff',
          cursor: '#ffffff',
          black: '#000000',
          red: '#e06c75',
          green: '#98c379',
          yellow: '#f0c674',
          blue: '#61afef',
          magenta: '#c678dd',
          cyan: '#56b6c2',
          white: '#abb2bf',
          brightBlack: '#5c6370',
          brightRed: '#e06c75',
          brightGreen: '#98c379',
          brightYellow: '#f0c674',
          brightBlue: '#61afef',
          brightMagenta: '#c678dd',
          brightCyan: '#56b6c2',
          brightWhite: '#ffffff',
        },
        rows: 40,
        cols: 120,
        scrollback: 1000,
        convertEol: true,
        disableStdin: false,
        cursorStyle: 'block',
        allowTransparency: true,
        screenReaderMode: false,
        windowsMode: true,
        macOptionIsMeta: true,
        macOptionClickForcesSelection: true,
        rightClickSelectsWord: true,
        fastScrollModifier: 'alt',
        fastScrollSensitivity: 5,
        scrollSensitivity: 1,
        smoothScrollDuration: 0,
        drawBoldTextInBrightColors: true
      });

      // Add addons
      const fitAddon = new FitAddon();
      term.loadAddon(fitAddon);
      term.loadAddon(new WebLinksAddon());
      
      // Only load WebGL addon if supported
      try {
        const webglAddon = new WebglAddon();
        term.loadAddon(webglAddon);
    } catch (error) {
        console.warn('WebGL addon not supported:', error);
      }

      // Mount terminal
      term.open(terminalRef.current);
      fitAddon.fit();
      xtermRef.current = term;

      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        term.write('\r\n\x1B[1;31mError: Not authenticated. Please log in.\x1B[0m\r\n');
        return cleanup;
      }

      // Connect to WebSocket
      const isDevelopment = import.meta.env.DEV;
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsHost = isDevelopment ? window.location.host : 'api.opsquest.dev';
      const encodedToken = encodeURIComponent(token);
      const wsUrl = `${wsProtocol}://${wsHost}/v1/containers/${containerId}/terminal?token=${encodedToken}`;

      console.log('Terminal Configuration:', {
        isDevelopment,
        wsProtocol,
        wsHost,
        containerId,
        terminalSize: { rows: 40, cols: 120 }
      });

      // Initialize WebSocket
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!isMountedRef.current) {
          ws?.close();
          return;
        }
        setIsConnected(true);
        
        // Send initial connection message
        const initMessage = JSON.stringify({ 
          type: 'connect',
          containerId,
          rows: 40,
          cols: 120,
          term: 'xterm-256color'
        });
        console.log('Sending initial message:', initMessage);
        ws?.send(initMessage);
      };

      ws.onmessage = (event) => {
        if (!isMountedRef.current || !term) return;

        try {
          // Handle binary data (Blob)
          if (event.data instanceof Blob) {
            const reader = new FileReader();
            reader.onload = () => {
              if (!isMountedRef.current || !term) return;
              const data = reader.result as string;
              term.write(data);
            };
            reader.readAsText(event.data);
            return;
          }

          // Handle text data (JSON)
          const data = JSON.parse(event.data);
          console.log('Received message:', data);
          
          if (data.type === 'error') {
            term.write(`\r\n\x1B[1;31mError: ${data.message}\x1B[0m\r\n`);
          } else if (data.type === 'output') {
            term.write(data.content);
          } else if (data.type === 'status') {
            console.log('Status update:', data);
            if (data.status === 'ready') {
              // Don't write the ready message, just ensure terminal is ready
              fitAddon.fit();
              term.resize(120, 40);
            }
          } else if (data.type === 'size') {
            console.log('Size acknowledged:', data);
          } else {
            term.write(event.data);
          }
        } catch (e) {
          console.error('Error handling message:', e);
          term.write(event.data);
        }
      };

      ws.onclose = (event) => {
        if (!isMountedRef.current) return;
        
        setIsConnected(false);
        console.log('WebSocket closed:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          timestamp: new Date().toISOString()
        });
        
        term?.write(`\r\n\x1B[1;31mConnection closed (${event.code}): ${event.reason || 'No reason provided'}\x1B[0m\r\n`);
        
        if ((event.code === 1006 || event.code === 1011) && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current);
          reconnectAttemptsRef.current += 1;
          
          term?.write(`\r\n\x1B[1;33mConnection lost. Attempting to reconnect in ${delay/1000} seconds... (Attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})\x1B[0m\r\n`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              console.log('Attempting to reconnect...');
              ws = new WebSocket(wsUrl);
              wsRef.current = ws;
            }
          }, delay);
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          term?.write('\r\n\x1B[1;31mMaximum reconnection attempts reached. Please refresh the page.\x1B[0m\r\n');
          setReconnectFailed(true);
        }
      };

      ws.onerror = (error) => {
        if (!isMountedRef.current) return;
        console.error('WebSocket error:', {
          error,
          readyState: ws?.readyState,
          timestamp: new Date().toISOString()
        });
        term?.write('\r\n\x1B[1;31mConnection error. Please try refreshing the page.\x1B[0m\r\n');
      };

      // Handle terminal input with proper key handling
      term.onData((data) => {
        if (ws?.readyState === WebSocket.OPEN) {
          try {
            // Handle special keys
            if (data.charCodeAt(0) === 13) { // Enter key
              ws.send('\r\n');
            } else if (data.charCodeAt(0) === 127) { // Backspace
              ws.send('\b');
            } else {
              console.log('Sending input:', data);
              ws.send(data);
            }
          } catch (e) {
            console.error('Error sending data:', e);
            term?.write('\r\n\x1B[1;31mError sending data to server\x1B[0m\r\n');
          }
        }
      });

      // Handle window resize with debounce
      const handleResize = () => {
        if (!isMountedRef.current || !term) return;
        
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }
        
        resizeTimeoutRef.current = setTimeout(() => {
          if (!term) return;
          
          const rows = 40;
          const cols = 120;
          term.resize(cols, rows);
          fitAddon.fit();
          
          if (ws?.readyState === WebSocket.OPEN) {
            console.log('Resizing terminal:', { rows, cols });
            const resizeMessage = JSON.stringify({ 
              type: 'resize', 
              rows, 
              cols
            });
            console.log('Sending resize message:', resizeMessage);
            ws.send(resizeMessage);
          }
        }, 100); // Debounce resize events
      };

      // Add resize observer for container
      resizeObserverRef.current = new ResizeObserver(handleResize);
      if (terminalRef.current) {
        resizeObserverRef.current.observe(terminalRef.current);
      }

      window.addEventListener('resize', handleResize);

      // Initial resize
      handleResize();

    } catch (error) {
      console.error('Error initializing terminal:', error);
      cleanup();
    }

    return cleanup;
  }, [containerId]);

  useEffect(() => {
    if (reconnectFailed && onSessionExpired) {
      onSessionExpired();
    }
  }, [reconnectFailed, onSessionExpired]);

  const openInNewWindow = () => {
    if (!containerId) return;
    const url = `/standalone-terminal?containerId=${containerId}`;
    const width = window.screen.availWidth - 100;
    const height = window.screen.availHeight - 100;
    window.open(
      url,
      '_blank',
      `width=${width},height=${height},left=50,top=50,resizable,scrollbars`
    );
  };

  return (
    <div className="bg-gray-900 rounded-lg border border-gray-700 flex flex-col h-[60vh] font-mono text-sm relative">
      {/* Terminal Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 rounded-t-lg border-b border-gray-700">
        <div className="flex items-center space-x-2">
          <TerminalIcon className="w-4 h-4 text-green-400" />
          <span className="text-white font-medium">Terminal</span>
          {isConnected && (
            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
              CONNECTED
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {containerId && challengeId && (
            <button
              onClick={validateSolution}
              disabled={validating}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold shadow px-3 py-1 rounded transition-colors flex items-center space-x-1 border border-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Validate solution"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              <span>{validating ? 'Validating...' : 'Validate'}</span>
            </button>
          )}
          {containerId && (
          <button
              onClick={openInNewWindow}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow px-3 py-1 rounded transition-colors flex items-center space-x-1 border border-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
              title="Open in new window"
              style={{ marginRight: 4 }}
          >
              <ExternalLink className="w-4 h-4 mr-1" />
              <span>New Window</span>
          </button>
          )}
          <div className="flex space-x-1">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Terminal Content */}
      <div 
        ref={terminalRef}
        className="flex-1 p-4 overflow-hidden bg-gray-900"
      />
      {reconnectFailed && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 z-10">
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
            <p className="text-red-300 mb-4">Maximum reconnection attempts reached.</p>
            <button
              className="btn-primary"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
